import React, {FC, useState, useEffect} from 'react'
import {Modal, Button, Spinner, Form, Alert} from 'react-bootstrap'
import {trelloApiService} from '../services/trelloApi'

interface TrelloOAuthModalProps {
  show: boolean
  onClose: () => void
  onSuccess: (integration: any) => void
}

export const TrelloOAuthModal: FC<TrelloOAuthModalProps> = ({
  show,
  onClose,
  onSuccess
}) => {
  const [authUrl, setAuthUrl] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null)
  
  // Estados para configuração das chaves da API
  const [apiKey, setApiKey] = useState('')
  const [apiSecret, setApiSecret] = useState('')
  const [showConfigFields, setShowConfigFields] = useState(false)
  const [isConfiguring, setIsConfiguring] = useState(false)
  const [configError, setConfigError] = useState<string | null>(null)

  // Verifica as chaves da API e gera URL de autorização quando o modal abre
  useEffect(() => {
    if (show) {
      checkApiKeys()
    }
  }, [show])

  const checkApiKeys = async () => {
    try {
      setIsLoading(true)
      setError(null)
      setConfigError(null)
      
      // ✅ SOLUÇÃO: Sempre verifica o status real da integração
      const status = await trelloApiService.getStatus()
      
      if (status.is_connected) {
        // Se está conectado, gera URL de autorização
        setShowConfigFields(false)
        generateAuthUrl()
      } else {
        // Se não está conectado, verifica se tem chaves da API
        const hasApiKeys = await trelloApiService.hasApiKeys()
        
        if (hasApiKeys) {
          // Se tem chaves mas não está conectado, gera URL de autorização
          setShowConfigFields(false)
          generateAuthUrl()
        } else {
          // Se não tem chaves, mostra campos de configuração
          setShowConfigFields(true)
          setIsLoading(false)
        }
      }
    } catch (error: any) {
      console.error('Error checking integration status:', error)
      // Em caso de erro, assume que não tem chaves e mostra configuração
      setShowConfigFields(true)
      setIsLoading(false)
    }
  }

  const generateAuthUrl = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      const returnUrl = `${window.location.origin}/admin/integrations/trello-callback`
      const url = await trelloApiService.getAuthorizationUrl(returnUrl)
      
      setAuthUrl(url)
    } catch (error: any) {
      console.error('Error generating auth URL:', error)
      setError('Erro ao gerar URL de autorização. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveApiKeys = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!apiKey.trim() || !apiSecret.trim()) {
      setConfigError('Por favor, preencha todas as chaves')
      return
    }

    try {
      setIsConfiguring(true)
      setConfigError(null)
      
      // Salva as chaves no backend
      await trelloApiService.saveApiKeys(apiKey.trim(), apiSecret.trim())
      
      // Após salvar as chaves, gera URL de autorização
      setShowConfigFields(false)
      setApiKey('')
      setApiSecret('')
      generateAuthUrl()
      
    } catch (error: any) {
      console.error('Error saving API keys:', error)
      setConfigError(error.message || 'Erro ao salvar as chaves da API')
    } finally {
      setIsConfiguring(false)
    }
  }

  const handleClose = () => {
    if (!isProcessing && !isConfiguring) {
      // Limpa o polling se estiver ativo
      if (pollingInterval) {
        clearInterval(pollingInterval)
        setPollingInterval(null)
      }
      
      // Limpa os campos de configuração
      setApiKey('')
      setApiSecret('')
      setShowConfigFields(false)
      setConfigError(null)
      
      onClose()
    }
  }

  const startPolling = (newWindow: Window) => {
    setIsProcessing(true)
    
    // Inicia polling a cada 2 segundos para verificar o status da integração
    const interval = setInterval(async () => {
      try {
        // Verifica se a nova janela foi fechada
        if (newWindow.closed) {
          clearInterval(interval)
          setPollingInterval(null)
          setIsProcessing(false)
          return
        }
        
        // Verifica o status da integração
        const status = await trelloApiService.getStatus()
        
        // Se a integração foi conectada, para o polling e notifica sucesso
        if (status.is_connected) {
          clearInterval(interval)
          setPollingInterval(null)
          setIsProcessing(false)
          
          // Fecha a nova janela
          newWindow.close()
          
          // Notifica sucesso
          onSuccess(status.integration)
          onClose()
          
          return
        }
      } catch (error) {
        console.error('Error during polling:', error)
        // Continua o polling mesmo com erro
      }
    }, 2000)
    
    setPollingInterval(interval)
  }

  const handleMessage = async (event: MessageEvent) => {
    // Verifica se a mensagem é do callback do Trello
    if (event.origin !== window.location.origin) return
    
    try {
      const {type, data} = event.data
      
      if (type === 'TRELLO_OAUTH_SUCCESS') {
        setIsProcessing(true)
        
        // Processa o token recebido
        const {access_token, trello_user_id, trello_username} = data
        
        // Conecta a integração
        const integration = await trelloApiService.connect(access_token, trello_user_id, trello_username)
        
        // Fecha o modal e notifica sucesso
        onSuccess(integration)
        onClose()
      } else if (type === 'TRELLO_OAUTH_ERROR') {
        setError(data.message || 'Erro na autorização do Trello')
      }
    } catch (error: any) {
      console.error('Error processing OAuth callback:', error)
      setError('Erro ao processar autorização. Tente novamente.')
    } finally {
      setIsProcessing(false)
    }
  }

  // Escuta mensagens do iframe
  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])
  
  // Cleanup do polling quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval)
      }
    }
  }, [pollingInterval])

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      size="lg"
      backdrop={isProcessing ? 'static' : true}
      keyboard={!isProcessing}
    >
      <Modal.Header closeButton={!isProcessing}>
        <Modal.Title>
          <i className='fab fa-trello text-primary me-2'></i>
          Conectar com Trello
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {isLoading ? (
          <div className='text-center py-5'>
            <Spinner animation="border" variant="primary" />
            <p className='mt-3 text-muted'>Verificando configuração...</p>
          </div>
        ) : showConfigFields ? (
          <div>
            <div className='mb-4'>
              <i className='fab fa-trello text-primary fs-1 mb-3 d-block text-center'></i>
              <h5 className='text-center'>Configurar Chaves da API do Trello</h5>
              <p className='text-muted text-center'>
                Para conectar com o Trello, você precisa configurar as chaves da API.
                <br />
                Após configurar as chaves, você poderá autorizar o TaskRush.
              </p>
            </div>
            
            {/* ✅ BOTÃO PARA RECONFIGURAR CHAVES */}
            <div className='text-center mb-4'>
              <Button 
                variant="outline-warning" 
                size="sm"
                onClick={() => {
                  setShowConfigFields(true)
                  setApiKey('')
                  setApiSecret('')
                  setConfigError(null)
                }}
              >
                <i className='fas fa-key me-2'></i>
                Reconfigurar Chaves da API
              </Button>
            </div>
            
            <div className='mb-4'>
              <Alert variant="info">
                <i className='fas fa-info-circle me-2'></i>
                <strong>Como obter as chaves:</strong>
                <ol className='mb-0 mt-2'>
                  <li>Acesse <a href="https://trello.com/power-ups/admin/" target="_blank" rel="noopener noreferrer">https://trello.com/power-ups/admin/</a></li>
                  <li>Faça login na sua conta Trello</li>
                  <li>Copie a <strong>API Key</strong> e <strong>API Secret</strong></li>
                  <li><strong>IMPORTANTE:</strong> No campo "Allowed origins", adicione:</li>
                  <ul className='mt-2 mb-0'>
                    <li><code>https://app.taskrush.com.br</code></li>
                  </ul>
                  <li>Cole as chaves nos campos abaixo</li>
                </ol>
              </Alert>
            </div>
            
            <Form onSubmit={handleSaveApiKeys}>
              <Form.Group className='mb-3'>
                <Form.Label>
                  <strong>API Key</strong> <span className='text-danger'>*</span>
                </Form.Label>
                <Form.Control
                  type='text'
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder='Cole sua API Key aqui'
                  disabled={isConfiguring}
                  required
                />
                <Form.Text className='text-muted'>
                  Chave pública da API do Trello
                </Form.Text>
              </Form.Group>
              
              <Form.Group className='mb-4'>
                <Form.Label>
                  <strong>API Secret</strong> <span className='text-danger'>*</span>
                </Form.Label>
                <Form.Control
                  type='password'
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  placeholder='Cole sua API Secret aqui'
                  disabled={isConfiguring}
                  required
                />
                <Form.Text className='text-muted'>
                  Chave privada da API do Trello
                </Form.Text>
              </Form.Group>
              
              {configError && (
                <Alert variant="danger" className='mb-3'>
                  <i className='fas fa-exclamation-triangle me-2'></i>
                  {configError}
                </Alert>
              )}
              
              <div className='d-grid gap-2'>
                <Button 
                  type="submit" 
                  variant="primary" 
                  size="lg"
                  disabled={isConfiguring || !apiKey.trim() || !apiSecret.trim()}
                >
                  {isConfiguring ? (
                    <>
                      <Spinner animation="border" size="sm" className='me-2' />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <i className='fas fa-save me-2'></i>
                      Salvar Chaves e Continuar
                    </>
                  )}
                </Button>
                
                <Button 
                  variant="outline-secondary" 
                  onClick={handleClose}
                  disabled={isConfiguring}
                >
                  Cancelar
                </Button>
              </div>
            </Form>
          </div>
        ) : error ? (
          <div className='text-center py-5'>
            <i className='fas fa-exclamation-triangle text-warning fs-1 mb-3'></i>
            <h5 className='text-danger mb-3'>Erro na Autorização</h5>
            <p className='text-muted mb-4'>{error}</p>
            <Button variant="primary" onClick={generateAuthUrl}>
              Tentar Novamente
            </Button>
          </div>
        ) : authUrl ? (
          <div className='text-center'>
            <div className='mb-4'>
              <i className='fab fa-trello text-primary fs-1 mb-3'></i>
              <h5>Autorizar TaskRush no Trello</h5>
              <p className='text-muted'>
                Clique no botão abaixo para autorizar o TaskRush a acessar sua conta do Trello.
                <br />
                Você será redirecionado para o Trello para confirmar a autorização.
              </p>
            </div>
            
            <div className='d-grid gap-2'>
              <Button 
                variant="primary" 
                size="lg"
                onClick={() => {
                  // Abre em nova aba para não perder o contexto
                  const newWindow = window.open(authUrl, '_blank', 'width=600,height=700')
                  
                  // Inicia polling para verificar se a integração foi conectada
                  if (newWindow) {
                    startPolling(newWindow)
                  }
                }}
                disabled={isProcessing}
              >
                <i className='fab fa-trello me-2'></i>
                Autorizar no Trello
              </Button>
              
              {/* ✅ BOTÃO PARA RECONFIGURAR CHAVES */}
              <Button 
                variant="outline-warning" 
                size="sm"
                onClick={() => {
                  setShowConfigFields(true)
                  setApiKey('')
                  setApiSecret('')
                  setConfigError(null)
                  setAuthUrl('')
                }}
              >
                <i className='fas fa-key me-2'></i>
                Reconfigurar Chaves da API
              </Button>
              
              <Button 
                variant="outline-secondary" 
                onClick={handleClose}
                disabled={isProcessing}
              >
                Cancelar
              </Button>
            </div>
            
            {isProcessing && (
              <div className='mt-4 text-center'>
                <Spinner animation="border" size="sm" variant="primary" />
                <p className='mt-2 text-muted'>
                  Processando autorização... Não feche esta janela.
                </p>
              </div>
            )}
          </div>
        ) : null}
      </Modal.Body>
    </Modal>
  )
}

export default TrelloOAuthModal
